import { makeAutoObservable } from 'mobx';
import GoogleDirectoryApi from 'main/containers/employees/google_import/GoogleDirectoryApi';
import GoogleUserViewModel from './GoogleUserViewModel';
import _ from 'lodash';
import {endpoints} from 'main/tools';
import {DomainStore} from 'shared/store';
import {extractGoogleError, reportError} from 'main/containers/employees/google_import/tools';

class GoogleImportStore extends DomainStore {
  async postUsers(users) {
    return this.post(
      endpoints.EMPLOYEE.BULK_IMPORT,
      'employeeImports',
      {
        employees: users.map(user => user.toJS())
      }
    );
  }
}

class GoogleImportState {
  googleDirectoryApi;
  store = new GoogleImportStore();
  onEmployeesAddedCallback;

  modalOpen = false;
  currentStep;
  googleUsers = [];
  selectAll = false;
  createdEmployees = [];
  duplicateEmployees = [];
  error;
  googleErrorMessage;

  constructor(onEmployeesAddedCallback) {
    makeAutoObservable(this, {
      store: false
    });

    this.onEmployeesAddedCallback = onEmployeesAddedCallback;
  }

  async launchImport() {
    this.currentStep = 'loading-google-init';
    this.modalOpen = true;
    this.googleDirectoryApi = new GoogleDirectoryApi({
      onUsersReceived: response => this.onUsersReceived(response),
      onError: (e) => this.onError(e)
    });
    const isSignedIn = await this.googleDirectoryApi.load();
    this.currentStep = isSignedIn ? 'loading-google-response' : 'start';
  }

  onError(e) {
    this.currentStep = 'finish';
    const {code, message} = extractGoogleError(e);
    this.error = code;
    this.googleErrorMessage = message;
    reportError(this.error);
  }

  cancelImport() {
    this.modalOpen = false;
    this.googleDirectoryApi.signOut();
    this.error = '';
    this.googleErrorMessage = '';
  }

  toggleSelectAll() {
    this.selectAll = !this.selectAll;
    for (const user of this.googleUsers) {
      user.selected = this.selectAll;
    }
  }

  signIn() {
    this.currentStep = 'loading-google-response';
    this.googleDirectoryApi.signIn();
  }

  async addEmployees() {
    const {model} = await this.store.postUsers(this.selectedUsers);

    if (model.duplicateEmployees) {
      this.duplicateEmployees = model.duplicateEmployees;
    }
    if (model.createdEmployees) {
      this.createdEmployees = model.createdEmployees;
      this.onEmployeesAddedCallback(model.createdEmployees);
    }
    this.currentStep = 'finish';
  }

  onUsersReceived(response) {
    if (response.status === 200 && response.result && response.result.users) {
      this.googleUsers = response.result.users.map(
        user => new GoogleUserViewModel(user)
      );
    }
    this.currentStep = 'select-employees';
  }

  async disconnectGoogleAccount() {
    await this.googleDirectoryApi.disconnect();
    this.currentStep = 'start';
  }

  get selectedUsers() {
    return _.filter(this.googleUsers, 'selected');
  }

  get createdEmployeesCount() {
    return this.createdEmployees.length;
  }

  get duplicateEmployeesCount() {
    return this.duplicateEmployees.length;
  }

  get isSignedIn() {
    return !!this.googleDirectoryApi.getCurrentUser();
  }

  get currentUser() {
    return this.googleDirectoryApi.getCurrentUser();
  }

  get hasError() {
    return !!this.error;
  }
}

export default GoogleImportState;
